import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { api, ApiError } from "../api";
import { CheckboxList, InlineAlert } from "../company-ui";
import { icons } from "../company-ui/icons";
import { ROLE_LINE, ROLE_RANK, rankRoles, roleLabel, rolesList, rolesOf, twoStepRolesOf, type RoleId } from "./roles";
import type { SignInSummary } from "./SignInAdmin";
import "./roles-picker.css";

/**
 * The Roles cell and its picker (ALN-121, roles.md B): a button on each row an administrator may
 * change, opening a popover under it (a bottom sheet on phones) with one checkbox per role. Adding or
 * removing Owner asks first. The server checks everything again.
 */

export interface RolesMember { id: string; name: string; role: string; roles?: string[]; version?: number; status: string; you: boolean; signIn?: SignInSummary }
interface PickerSettings { settings: { pinRoles: string[]; twoStepScope: string; twoStepRoles: string[] } }
export interface RolesSaved { user: RolesMember; temporaryPassword?: string }

const SHOWN = 3;
export const rolesButtonId = (userId: string) => `rb-${userId}`;

/** May this administrator change this row's roles? Not your own, not a deactivated person, an owner's only for an owner. */
export const rolesEditable = (u: RolesMember, viewerOwner: boolean) => !u.you && u.status !== "deactivated" && (viewerOwner || !rolesOf(u).includes("owner"));

export function RolesCell({ u, viewerOwner, open, onOpen }: { u: RolesMember; viewerOwner: boolean; open: boolean; onOpen: () => void }) {
  const rs = rolesOf(u);
  const shown = rs.slice(0, SHOWN);
  const more = rs.length - shown.length;
  const moreTag = more > 0 ? <span className="roles__more"> +{more} more</span> : null;
  if (!rolesEditable(u, viewerOwner)) return <span className="roles__txt">{rolesList(shown)}{moreTag}</span>;
  return (
    <button type="button" className="rolesbtn" id={rolesButtonId(u.id)} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? "roles-picker" : undefined}
      aria-label={rolesName(u.name, rs, shown, more)} onClick={onOpen}>
      <span className="rolesbtn__t">{rolesList(shown)}</span>{moreTag}
      <span className="rolesbtn__chev" aria-hidden="true">{icons.chevron}</span>
    </button>
  );
}

/** The button's accessible name carries its visible text (WCAG 2.5.3), so the list reads as shown: "Winemaker, Cellar". */
const rolesName = (name: string, rs: string[], shown: string[], more: number) =>
  `Change roles for ${name}, now ${rolesList(shown)}${more > 0 ? ` +${more} more (${rolesList(rs)})` : ""}`;

type Step = "pick" | "make" | "remove";

export function RolesPicker({ u, viewerOwner, settings, onClose, onSaved, reload }: {
  u: RolesMember; viewerOwner: boolean; settings: PickerSettings | undefined;
  onClose: (refocus: boolean) => void; onSaved: (r: RolesSaved) => void;
  /** The current list, after an edit conflict. */
  reload: () => Promise<RolesMember | undefined>;
}) {
  const before = rolesOf(u);
  const [roles, setRoles] = useState<RoleId[]>(before);
  const [base, setBase] = useState<{ roles: RoleId[]; version?: number }>({ roles: before, version: u.version });
  const [step, setStep] = useState<Step>("pick");
  const [reason, setReason] = useState("");
  const [why, setWhy] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [serverPin, setServerPin] = useState(false);
  const [refocus, setRefocus] = useState(0);
  const [ownerFocus, setOwnerFocus] = useState(false);
  const pop = useRef<HTMLDivElement & HTMLFormElement>(null);
  const titleId = useId();
  const descId = useId();
  const noneId = useId();
  const errId = useId();

  const after = rankRoles(roles);
  const none = after.length === 0;
  const pickable = ROLE_RANK.filter(r => r !== "owner" || viewerOwner);
  const needs2 = twoStepRolesOf(settings?.settings);
  const needs = (rs: readonly string[]) => rs.some(r => needs2.includes(r));
  const twoStep = u.signIn?.twoStep !== "on" && needs(after) && !needs(base.roles);
  const pinRoles = settings?.settings.pinRoles ?? [];
  // the role that can't use a PIN; after a 409 pin_switch_needed the server's message names it, so no second guess here
  const pinStop = u.signIn?.method === "pin" ? after.find(r => !pinRoles.includes(r)) : undefined;
  const pinSwitch = !!pinStop || (serverPin && u.signIn?.method === "pin");

  // place the popover under its button (above it when there's no room below); phones get a bottom sheet from CSS
  useLayoutEffect(() => {
    const place = () => {
      const el = pop.current; const b = document.getElementById(rolesButtonId(u.id)); const parent = el?.offsetParent as HTMLElement | null;
      if (!el || !b || !parent || window.innerWidth <= 600) { el?.style.removeProperty("top"); el?.style.removeProperty("left"); return; }
      const pr = parent.getBoundingClientRect(), br = b.getBoundingClientRect(); const w = el.offsetWidth, h = el.offsetHeight;
      const below = br.bottom - pr.top + 6, above = br.top - pr.top - h - 6;
      el.style.top = `${Math.round(br.bottom + 6 + h > window.innerHeight && above >= 0 ? above : below)}px`;
      el.style.left = `${Math.round(Math.max(0, Math.min(br.left - pr.left, pr.width - w)))}px`;
    };
    place();
    window.addEventListener("resize", place);
    // the table scrolls sideways at tablet widths, which moves the button
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  });
  // focus the heading when the picker or a confirm opens
  // back from a confirm, focus lands on the Owner box the person changed (mockup v35)
  useEffect(() => {
    const owner = ownerFocus && step === "pick" ? pop.current?.querySelector<HTMLInputElement>("input[data-value=owner]") : null;
    (owner ?? document.getElementById(titleId))?.focus();
    if (ownerFocus) setOwnerFocus(false);
  }, [step, titleId, refocus]); // eslint-disable-line react-hooks/exhaustive-deps
  // a click outside closes it; focus returns to the row's button unless the click landed on something focusable
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      // the backdrop handles its own tap; a click outside a confirm never drops the pending owner change
      if (pop.current?.contains(t) || t.classList.contains("rp-backdrop") || document.getElementById(rolesButtonId(u.id))?.contains(t)) return;
      onClose(!t.closest("a, button, input, select, textarea, summary, [tabindex]"));
    };
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, [u.id, onClose]);

  const backToPick = () => {
    // Cancel on Make owner and Keep on Remove as owner put Owner back as it was
    setRoles(step === "make" ? roles.filter(r => r !== "owner") : rankRoles([...roles, "owner"]));
    setStep("pick"); setErr(""); setWhy(""); setOwnerFocus(true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Tab") { if (modal()) trap(e); return; }
    if (e.key !== "Escape" || saving) return;
    e.preventDefault(); e.stopPropagation();
    // Escape in a confirm goes back to the picker, like Cancel or Keep; in the picker it closes it
    if (step === "pick") onClose(true); else backToPick();
  };
  // the confirms and the phone sheet are modal: focus stays inside. The desktop picker is a popover: Tab out closes it.
  const modal = () => step !== "pick" || window.innerWidth <= 600;
  const trap = (e: KeyboardEvent) => {
    const all = Array.from(pop.current?.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), [tabindex='-1']") ?? []).filter(x => x.tabIndex >= 0);
    const first = all[0], last = all[all.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && (document.activeElement === first || document.activeElement === document.getElementById(titleId))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const commit = async (ownerWhy: string) => {
    if (after.join() === base.roles.join()) { onClose(true); return; } // the same set: nothing to save
    setSaving(true); setErr("");
    try {
      const r = await api<RolesSaved>(`/users/${u.id}`, { method: "PATCH", body: { roles: after, version: base.version, reason: (ownerWhy || reason).trim() || undefined, ...(pinSwitch ? { switchToPassword: true } : {}) } });
      onSaved(r);
    } catch (e) {
      setSaving(false); setStep("pick"); setRefocus(n => n + 1);
      if (e instanceof ApiError && e.code === "edit_conflict") {
        const fresh = await reload().catch(() => undefined);
        if (fresh) { const rs = rolesOf(fresh); setRoles(rs); setBase({ roles: rs, version: fresh.version }); }
      }
      if (e instanceof ApiError && e.code === "pin_switch_needed") setServerPin(true);
      setErr(e instanceof ApiError ? e.message : "Something went wrong. Try again.");
    }
  };
  const save = () => {
    if (none || saving) return;
    const owner = (rs: readonly string[]) => rs.includes("owner");
    if (!owner(base.roles) && owner(after)) { setStep("make"); setErr(""); return; }
    if (owner(base.roles) && !owner(after)) { setStep("remove"); setErr(""); return; }
    void commit("");
  };
  const removeSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!why.trim()) { setErr("Say why you're removing an owner."); document.getElementById("roles-why")?.focus(); return; }
    void commit(why);
  };
  const errBox = err ? <div id={errId}><InlineAlert variant="error" title={err} /></div> : null;

  // a backdrop behind the confirms (every width) and the phone sheet; a tap on it is Cancel or Keep, or closes the picker
  const backdrop = <div className={`rp-backdrop${step === "pick" ? " rp-backdrop--sheet" : ""}`} aria-hidden="true" onClick={() => { if (!saving) { if (step === "pick") onClose(true); else backToPick(); } }} />;
  if (step === "make") {
    return (<>{backdrop}
      <div className="rp rp--confirm" id="roles-picker" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} ref={pop} onKeyDown={onKey}>
        <h3 className="rp__t" id={titleId} tabIndex={-1}>Make {u.name} an owner?</h3>
        <p className="rp__d" id={descId}>Owners can change everything, including other owners' sign-in.</p>
        {errBox}
        <div className="rp__acts">
          <button type="button" className="btn btn--primary" disabled={saving} onClick={() => void commit("")}>{saving ? "Saving…" : "Make owner"}</button>
          <button type="button" className="btn" disabled={saving} onClick={backToPick}>Cancel</button>
        </div>
      </div></>
    );
  }
  if (step === "remove") {
    return (<>{backdrop}
      <form className="rp rp--confirm" id="roles-picker" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} ref={pop} onKeyDown={onKey} onSubmit={removeSubmit} noValidate>
        <h3 className="rp__t" id={titleId} tabIndex={-1}>Remove {u.name} as owner?</h3>
        <p className="rp__d" id={descId}>They keep the other roles checked here.</p>
        <label className="field" htmlFor="roles-why">
          <span className="field__label">Say why (required)</span>
          <input className="input" id="roles-why" maxLength={500} autoComplete="off" value={why} onChange={e => { setWhy(e.target.value); setErr(""); }}
            aria-invalid={err ? true : undefined} aria-describedby={err ? errId : undefined} />
        </label>
        {errBox}
        <div className="rp__acts">
          <button type="submit" className="btn btn--destructive" disabled={saving}>{saving ? "Saving…" : "Remove as owner"}</button>
          <button type="button" className="btn" disabled={saving} onClick={backToPick}>Keep</button>
        </div>
      </form></>
    );
  }
  return (<>{backdrop}
    <div className="rp" id="roles-picker" role="dialog" aria-labelledby={titleId} aria-busy={saving || undefined} ref={pop} onKeyDown={onKey}>
      <h3 className="rp__t" id={titleId} tabIndex={-1}>Roles for {u.name}</h3>
      <CheckboxList legend="Roles" hideLegend options={roleOptions(pickable)} value={after} disabled={saving} describedBy={none ? noneId : undefined}
        onChange={v => { setRoles(rankRoles(v as RoleId[])); setErr(""); }} />
      <p className={none ? "small rp__none" : "small muted m0"} id={noneId} aria-live="polite">{none ? "Choose at least one role." : `${u.name} can do everything any of these roles can.`}</p>
      {!none && twoStep && <InlineAlert variant="warning" title={`${u.name} will need to set up two-step sign-in at their next sign-in, and loses access until they do.`} />}
      {!none && pinStop && <InlineAlert variant="warning" title={`${u.name} signs in with a PIN, which ${roleLabel(pinStop)} can't use here. Saving switches them to a password: you'll get a temporary password to give them in person, and their PIN sessions end.`} />}
      <label className="field" htmlFor="roles-reason">
        <span className="field__label">Reason (optional)</span>
        <input className="input" id="roles-reason" maxLength={500} autoComplete="off" value={reason} disabled={saving} onChange={e => setReason(e.target.value)} />
      </label>
      {errBox}
      <div className="rp__acts">
        <button type="button" className="btn btn--primary" disabled={none || saving} onClick={save}>{saving ? "Saving…" : pinSwitch ? "Save roles and switch to password" : "Save roles"}</button>
        <button type="button" className="btn" disabled={saving} onClick={() => onClose(true)}>Cancel</button>
      </div>
    </div></>
  );
}

/** The invite form's Roles list (roles.md B, Inviting): the same checkboxes, Estimator by default, Owner only for an owner. */
export function RolesChecklist({ value, onChange, viewerOwner }: { value: string[]; onChange: (v: RoleId[]) => void; viewerOwner: boolean }) {
  return (
    <CheckboxList legend="Roles" name="roles" className="field rp__list--form" options={roleOptions(ROLE_RANK.filter(r => r !== "owner" || viewerOwner))}
      value={value} onChange={v => onChange(rankRoles(v as RoleId[]))} error={value.length ? null : "Choose at least one role."} />
  );
}

const roleOptions = (rs: readonly RoleId[]) => rs.map(r => ({ value: r, label: roleLabel(r), line: ROLE_LINE[r] }));
